import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import styles from './LevelBadge.module.css';
import { getLevelBadge } from './LevelBadges';

const LEVELS = [
    { name: 'Beginner', xp: 0, description: 'You\'re just starting your journey.' },
    { name: 'Novice', xp: 500, description: 'Building fundamental speaking skills.' },
    { name: 'Intermediate', xp: 2000, description: 'Developing consistent speaking habits.' },
    { name: 'Advanced', xp: 5000, description: 'Mastering advanced speaking techniques.' },
    { name: 'Proficient', xp: 10000, description: 'Confident in most speaking situations.' },
    { name: 'Expert', xp: 18000, description: 'Exhibiting excellent speaking skills.' },
    { name: 'Elite', xp: 28000, description: 'Distinguished speaking abilities.' },
    { name: 'Master', xp: 40000, description: 'Exceptional speaking prowess.' },
    { name: 'Grandmaster', xp: 55000, description: 'Outstanding speaking excellence.' },
    { name: 'Legendary', xp: 75000, description: 'Peak speaking performance.' }
];

const LevelPanel = ({ isVisible, onClose }) => {
    const { user } = useAuth();
    const totalXp = user?.total_xp || 0;
    const [showTips, setShowTips] = useState(false);

    const currentLevel = LEVELS.reduce((prev, curr) => {
        if (totalXp >= curr.xp) return curr;
        return prev;
    }, LEVELS[0]);

    const nextLevel = LEVELS[LEVELS.indexOf(currentLevel) + 1];
    const progress = nextLevel
        ? ((totalXp - currentLevel.xp) / (nextLevel.xp - currentLevel.xp)) * 100
        : 100;

    const toggleTips = () => {
        setShowTips(!showTips);
    };

    return (
        <div className={`${styles.levelPanel} ${isVisible ? styles.visible : ''}`}>
            <div className={styles.panelHeader}>
                <h3>Your Speaking Level</h3>
                <button onClick={onClose} className={styles.closeButton}>
                    <X size={16} />
                </button>
            </div>

            <div className={styles.levelContent}>
                <div className={styles.currentLevelInfo}>
                    <div className={styles.levelIconContainer}>
                        {getLevelBadge(currentLevel.name, 120, isVisible)}
                    </div>
                    <div className={styles.levelDetails}>
                        <h4 className={styles.levelName}>{currentLevel.name}</h4>
                        <p className={styles.levelDescription}>{currentLevel.description}</p>
                        <div className={styles.xpCount}>
                            <span>{totalXp.toLocaleString()} XP</span>
                        </div>
                    </div>
                </div>

                {nextLevel && (
                    <div className={styles.progressSection}>
                        <div className={styles.progressHeader}>
                            <span>Progress to {nextLevel.name}</span>
                            <span>{Math.round(progress)}%</span>
                        </div>
                        <div className={styles.progressBar}>
                            <div
                                className={styles.progressFill}
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                        <div className={styles.xpNeeded}>
                            <span>{nextLevel.xp - totalXp} XP needed</span>
                        </div>
                    </div>
                )}

                <div className={styles.collapsibleSection}>
                    <button
                        className={styles.collapsibleButton}
                        onClick={toggleTips}
                        aria-expanded={showTips}
                    >
                        <span>How to Earn XP</span>
                        {showTips ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>

                    {showTips && (
                        <ul className={styles.tipsList}>
                            <li>Complete practice sessions</li>
                            <li>Maintain your daily streak</li>
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LevelPanel;